import { useEffect, useState } from 'react';

// How long an armed button waits for its second tap before going back to what it said. Long enough
// to read the question and answer it, short enough that a button nobody answered is never still
// asking when the page is looked at again.
export const CONFIRM_MS = 4000;

/**
 * Turns a button into its own confirmation: the first tap asks, the second does it.
 *
 * Returns `[armed, onClick]`. `onClick` goes on the button as it is, and `armed` is what the label
 * reads off. Nothing is rendered here, so the same hook serves a modal button, a toolbar icon or a
 * row in a menu, each keeping its own look.
 *
 * For an action small enough not to warrant a dialog over the whole page. A tap elsewhere doesn't
 * put the question away: most use here is on a phone, where there is no pointer leaving the button
 * to key off, so it disarms itself after `delay` instead.
 */
const useConfirmClick = (onConfirm, delay = CONFIRM_MS) => {
  const [armed, setArmed] = useState(false);

  useEffect(() => {
    if (!armed) return undefined;

    const timer = setTimeout(() => setArmed(false), delay);

    return () => clearTimeout(timer);
  }, [armed, delay]);

  const handleClick = () => {
    if (!armed) {
      setArmed(true);

      return;
    }

    // Put away before the action runs: it may take the button off the page with it
    setArmed(false);
    onConfirm();
  };

  return [armed, handleClick];
};

export default useConfirmClick;
